import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ConditionalRender
  from '@avtechsoftware/react-component-library/src/components/common/ConditionalRender/ConditionalRender';
import Icon from '@avtechsoftware/react-component-library/src/components/common/Icon/Icon';
import { Card } from 'react-bootstrap';
import axios from 'axios';
import { useSelector } from 'react-redux';
import QuickAlertForm from '../QuickAlertForm';
import RaaDefaultButton from '../../../../common/RaaDefaultButton/RaaDefaultButton';
import RaaAlertMapper from '../../../../../classes/mappers/RaaAlertMapper';
import style from '../QuickAlertForm.module.css';
import { connectModalContainerContextToComponent } from '../../../../../contexts/ModalContext/ModalContext';
import ClassUtility from '../../../../../utility/ClassUtility';
import CreateActionModal from '../../../NewAlertPage/NewAlertModals/CreateActionModal/CreateActionModal';
import NotificationActionCardBody
  from '../../../AlertDetailsPage/AlertActions/NotificationActionCard/NotificationActionCardBody';
import RaaAuthenticatedRemoteService from '../../../../../utility/RaaAuthenticatedRemoteService';
import { selectSessionInformation } from '../../../../../redux/slices/sessionSlice';
import { selectNotificationGroupList } from '../../../../../redux/slices/notificationGroupsSlice/notificationGroupsSlice';

function QuickAlertFormWrapper(props) {
  const {
    hasAdvancedAlerting,
    quickAlertList,
    setModalJSX,
    setShowConfirmationPage,
    setQuickAlertsData,
    alertLimit,
    users,
    initialNotificationGroupId,
  } = props;
  const sessionInformation = useSelector(selectSessionInformation);
  const notificationGroupList = useSelector(selectNotificationGroupList);
  const mappedQuickAlertList = RaaAlertMapper
    .mapJsonToQuickAlerts(quickAlertList, initialNotificationGroupId);

  const [quickAlerts, setQuickAlerts] = useState(mappedQuickAlertList);
  const [isEditingAction, setIsEditingAction] = useState(false);
  const [isDefaultAlerts, setIsDefaultAlerts] = useState(true);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [submitBtnText, setSubmitBtnText] = useState('Save');
  const [formErrors1, setFormErrors1] = useState([]);
  const [formErrors2, setFormErrors2] = useState([]);

  // eslint-disable-next-line max-len
  const isActionCountAtTierLimit = !hasAdvancedAlerting && quickAlerts[0].alertActionList.length > 0;

  const handleUpdateQuickAlerts = (quickAlertId, quickAlert) => {
    setIsDefaultAlerts(false);
    const newQuickAlerts = ClassUtility.cloneObject(quickAlerts);
    const currentAlertIndex = newQuickAlerts.findIndex((alert) => alert.id === quickAlertId);
    newQuickAlerts[currentAlertIndex] = quickAlert;
    setQuickAlerts(newQuickAlerts);
  };

  const handleDiscardAction = (index) => {
    const newQuickAlerts = [];
    const newActions = ClassUtility.cloneObject(quickAlerts[0].alertActionList);
    newActions.splice(index, 1);
    const updatedQuickAlert1 = ClassUtility.cloneObject(quickAlerts[0]);
    updatedQuickAlert1.alertActionList = newActions;
    newQuickAlerts.push(updatedQuickAlert1);
    if (quickAlerts.length > 1) {
      const updatedQuickAlert2 = ClassUtility.cloneObject(quickAlerts[1]);
      updatedQuickAlert2.alertActionList = newActions;
      newQuickAlerts.push(updatedQuickAlert2);
    }
    setQuickAlerts(newQuickAlerts);
  };

  const handleUpdateAlertActions = (alert) => {
    const newActions = alert.alertActionList;
    const updatedQuickAlerts = quickAlerts.map((quickAlert) => ({
      ...quickAlert,
      alertActionList: newActions,
    }));
    setQuickAlerts(updatedQuickAlerts);
  };
  const handleSubmitQuickAlerts = async (e) => {
    e.preventDefault();
    setFormErrors1([]);
    setFormErrors2([]);
    setIsFormSubmitting(true);
    setSubmitBtnText('Saving...');

    try {
      const quickAlertsData = [];
      const actionTestPromises = [];

      const createAlert = async (alertData) => {
        const response = await RaaAuthenticatedRemoteService.globalInstance().post(
          `${window.envConfig.apiUrl}/api/v1/alerts/`,
          { data: alertData },
        );

        if (!response.numberOfAlertsCreated || response.numberOfAlertsCreated !== 1) {
          throw new Error('Create alert validation rejected.');
        }

        return response.createdAlertIdList[0]; // Returning the created alert ID
      };

      const fetchAlertData = async (alertId) => {
        const response = await RaaAuthenticatedRemoteService.globalInstance().get(
          `${window.envConfig.apiUrl}/api/v1/alerts/data/${alertId}`,
        );
        return response;
      };

      const testAlertActions = async (alertId, actions) => {
        const sendTestNotification = async (recipientId) => {
          // eslint-disable-next-line no-useless-catch
          try {
            const response = await RaaAuthenticatedRemoteService.globalInstance()
              .post(`${window.envConfig.apiUrl}/api/v1/notification-group/recipient/test/${recipientId}`);
            if (!response.recipientId) {
              throw new Error(`Failed to send test notification for recipientId: ${recipientId}`);
            }
            return true;
          } catch (error) {
            throw error;
          }
        };

        // eslint-disable-next-line no-useless-catch
        try {
          await Promise.all(
            actions.map(async (action) => {
              const notificationGroups = notificationGroupList
                .filter((group) => action.notificationGroupIds.includes(group.id));

              await Promise.all(
                notificationGroups.map(async (group) => {
                  const { recipients } = group;

                  return Promise.all(
                    recipients.map((recipient) => sendTestNotification(recipient.id)),
                  );
                }),
              );
            }),
          );
          // success
        } catch (error) {
          throw error;
        }
      };

      const quickAlert1 = RaaAlertMapper.mapNewQuickAlertToJson(quickAlerts[0],
        sessionInformation);
      const alertId1 = await createAlert(quickAlert1);

      if (hasAdvancedAlerting) {
        // If advanced alerting, handle second quick alert
        await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait 1 second
        const quickAlert2 = RaaAlertMapper.mapNewQuickAlertToJson(quickAlerts[1],
          sessionInformation);
        const alertId2 = await createAlert(quickAlert2);

        const getAlertRes1 = await fetchAlertData(alertId1);
        quickAlertsData.push(getAlertRes1);

        const getAlertRes2 = await fetchAlertData(alertId2);
        quickAlertsData.push(getAlertRes2);

        setQuickAlertsData([...quickAlertsData]);

        await testAlertActions(alertId1, getAlertRes1.alertActions);
        await testAlertActions(alertId2, getAlertRes2.alertActions);

        await Promise.all(actionTestPromises);
      } else {
        const getAlertRes1 = await fetchAlertData(alertId1);

        quickAlertsData.push(getAlertRes1);
        setQuickAlertsData([...quickAlertsData]);

        const [firstAction] = getAlertRes1.alertActions;
        const params = new URLSearchParams();
        params.append('alertId', alertId1);
        params.append('alertActionId', firstAction.id);

        await axios.post('/alerts/data/test-alert-action', params);
      }

      setShowConfirmationPage(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error occurred:', error.message);
      setFormErrors1(['An error occurred while saving the alert.']);
      setIsFormSubmitting(false);
      setSubmitBtnText('Save');
    }
  };

  return (
    <div className="card">
      <div className="card-body pb-0">
        <ConditionalRender show={isDefaultAlerts}>
          <div className="card-body p-0">
            <h5 className="card-title">
              Alert Preview
            </h5>
            <ConditionalRender show={hasAdvancedAlerting || alertLimit > 1}>
              <p className="card-text border rounded p-3" style={{ borderColor: '#ccc', backgroundColor: '#A9C2E5' }}>
                {`After reviewing trends from your sensors, Room Alert Account recommends two alerts to get you started. 
           The first alert will notify you if your sensor's temperature goes above its average range. 
           The second alert will notify you if your device stops reporting new sensor data.`}
              </p>
            </ConditionalRender>
            <ConditionalRender show={!hasAdvancedAlerting}>
              <p className="card-text border rounded p-3" style={{ borderColor: '#ccc', backgroundColor: '#A9C2E5' }}>
                {`After reviewing trends from your sensors,
             Room Alert Account recommends the following alert to notify you 
             if your sensor's temperature goes above its average range.`}
              </p>
            </ConditionalRender>
          </div>
        </ConditionalRender>

        <ConditionalRender show={!isDefaultAlerts}>
          <div className="card-body p-0">
            <h5 className="card-title">
              Alert Preview
            </h5>
          </div>
        </ConditionalRender>
        <hr />
        <div className="card-body p-0">
          <QuickAlertForm
            initialAlert={quickAlerts[0]}
            errorList={formErrors1}
            handleUpdateQuickAlerts={handleUpdateQuickAlerts}
          />
        </div>

        <ConditionalRender show={hasAdvancedAlerting || alertLimit > 1}>
          <hr />
          <div className="card-body p-0">
            <QuickAlertForm
              initialAlert={quickAlerts[1]}
              errorList={formErrors2}
              handleUpdateQuickAlerts={handleUpdateQuickAlerts}
            />
          </div>
        </ConditionalRender>

        <hr />
        <ConditionalRender show={!isEditingAction}>
          <div className="card-body p-0">
            <div className="clearfix">
              <button
                type="button"
                className="float-right p-0 btn btn-link"
                onClick={() => setIsEditingAction(true)}
              >
                <Icon
                  icon="pencil"
                  className="text-muted"
                />
              </button>
            </div>
            <ConditionalRender show={!hasAdvancedAlerting}>
              <h5 className="card-title">
                When this alert triggers, Room Alert Account will perform this action...
              </h5>
            </ConditionalRender>
            <ConditionalRender show={hasAdvancedAlerting || alertLimit > 1}>
              <h5 className="card-title">
                {/* eslint-disable-next-line max-len */}
                When either of these alerts trigger, Room Alert Account will perform these action(s)...
              </h5>
            </ConditionalRender>
            <div className="row">
              <div className="col d-flex">
                <span className={style.targetSvg}>
                  <Icon icon="bolt" size="2x" className="fa-fw text-muted" />
                </span>
                <div className="ml-2">
                  {
                    quickAlerts[0].alertActionList.map((action, index) => (
                      <Card>
                        <Card.Header>
                          {`Notification Action ${index + 1}:`}
                        </Card.Header>
                        <Card.Body>
                          <NotificationActionCardBody
                            key={index}
                            action={action}
                            canDelete={false}
                          />
                        </Card.Body>
                      </Card>
                    ))
                  }
                </div>
              </div>
            </div>
          </div>
        </ConditionalRender>
        <ConditionalRender show={isEditingAction}>
          <div className="card-body py-3">
            <div className="form-group row">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="col-md-2 font-weight-bold">Actions</label>
              <div className="col">
                <div className="row">
                  {quickAlerts[0].alertActionList.map((action, index) => (
                    <div className="col-xl-6 col-sm-12 my-sm-1 border border-secondary rounded-sm" key={index}>
                      <div className="p-1">
                        <NotificationActionCardBody action={action} />
                        <button
                          type="button"
                          className="btn btn-default float-right p-0"
                          onClick={() => handleDiscardAction(index)}
                        >
                          <Icon icon="trash-can" className="text-muted" size="sm" />
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="col-6">
                    <div className="mb-3 pt-1">
                      <RaaDefaultButton
                        additionalClass="btn-sm"
                        disabled={isActionCountAtTierLimit}
                        onClick={() => {
                          setModalJSX((
                            <CreateActionModal
                              activeAlert={quickAlerts[0]}
                              setActiveAlert={handleUpdateAlertActions}
                              hasAdvancedAlerting={hasAdvancedAlerting}
                              users={users}
                            />
                          ));
                        }}
                      >
                        <Icon icon="plus" className="mr-1" />
                        Add Action
                      </RaaDefaultButton>
                    </div>
                  </div>
                </div>
              </div>
              <ConditionalRender show={isActionCountAtTierLimit}>
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label className="col-lg-2 control-label" />
                <div className="col-auto col-lg-10">
                  <div className="alert alert-info">
                    <div className="float-left mr-1">
                      <Icon icon="info-circle" />
                    </div>
                    <div style={{ marginLeft: '1.5em' }}>
                      <p>Your current account allows one action for each alert.</p>
                      <p>
                        For advanced functionality including alerts that support multiple
                        actions, repeat notifications,
                        comments, customized alert messages and much more, consider
                        {' '}
                        <a target="_blank" href="/subscription">upgrading</a>
                        {' '}
                        your account.
                        <a
                          target="_blank"
                          type="button"
                          className="btn btn-sm btn-light border-info py-0"
                          href="/s/subscription/billing/change-plan"
                          style={{ lineHeight: '1' }}
                        >
                          Upgrade Now
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </ConditionalRender>
            </div>
          </div>
        </ConditionalRender>
        <hr />
        <div className="text-center my-3">
          <button
            className="btn btn-primary"
            type="button"
            id="submit-quick-alerts-button"
            disabled={isFormSubmitting || !quickAlerts[0].alertActionList.length}
            onClick={handleSubmitQuickAlerts}
          >
            {submitBtnText}
          </button>
        </div>
      </div>
    </div>
  );
}

QuickAlertFormWrapper.propTypes = {
  hasAdvancedAlerting: PropTypes.bool.isRequired,
  quickAlertList: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      channelKey: PropTypes.string.isRequired,
      threshold: PropTypes.shape({
        operator: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
      }),
      temperatureScale: PropTypes.string.isRequired,
      action: PropTypes.shape({
        type: PropTypes.string.isRequired,
        emailAddress: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  ).isRequired,
  setModalJSX: PropTypes.func.isRequired,
  setShowConfirmationPage: PropTypes.func.isRequired,
  setQuickAlertsData: PropTypes.func.isRequired,
  alertLimit: PropTypes.number.isRequired,
  users: PropTypes.shape({
    users: PropTypes.arrayOf(
      PropTypes.shape({
        userId: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
      }),
    ),
  }).isRequired,
  initialNotificationGroupId: (PropTypes.string || PropTypes.number).isRequired,
};

export { QuickAlertFormWrapper };
export default connectModalContainerContextToComponent(QuickAlertFormWrapper);
